/**
 * ============================================
 * useSchedule.ts — Хук для загрузки расписания
 * ============================================
 * Функциональность:
 * - Загрузка данных через fetch (каждые 30 сек)
 * - SSE real-time обновления
 * - Автопереподключение при разрыве
 * - Кэширование в localStorage
 * - Оффлайн-режим
 */

import { useState, useEffect, useRef, useCallback } from 'react';

// ============================================
// Типы
// ============================================

/** Возможные статусы рейса */
export type BusStatus = 'scheduled' | 'boarding' | 'departed' | 'delayed' | 'cancelled';

/** Данные одного рейса автобуса */
export interface BusScheduleItem {
  /** Уникальный ID рейса */
  id: string;
  /** Время отправления в формате HH:MM */
  time: string;
  /** Номер маршрута */
  route: string;
  /** Цвет маршрута (hex) */
  routeColor: string;
  /** Направление / пункт назначения */
  direction: string;
  /** Номер платформы */
  platform: string;
  /** Текущий статус рейса */
  status: BusStatus;
  /** Задержка в минутах (только для status=delayed) */
  delayMinutes?: number;
  /** Время последнего обновления записи */
  updatedAt?: string;
}

/** Состояние подключения */
export type ConnectionState = 'online' | 'offline' | 'reconnecting';

/** Результат хука useSchedule */
export interface UseScheduleResult {
  /** Список рейсов */
  data: BusScheduleItem[];
  /** Загружаются ли данные */
  loading: boolean;
  /** Состояние подключения */
  connection: ConnectionState;
  /** Текст ошибки (если есть) */
  error: string | null;
  /** Показывать ли индикатор обновления */
  isUpdating: boolean;
  /** Ручное обновление данных */
  refetch: () => void;
}

interface ApiScheduleTrip {
  id: string;
  startTime: string;
  routeNumber?: string;
  routeColor?: string;
  routeName?: string;
  direction?: string;
  platform?: string | number;
  computedStatus?: BusStatus;
  status?: BusStatus;
}

interface ApiScheduleResponse {
  success: boolean;
  data?: {
    trips?: ApiScheduleTrip[];
  };
}

// ============================================
// Константы
// ============================================

/** Интервал автообновления данных (30 секунд) */
const REFRESH_INTERVAL = 30_000;

/** Таймаут SSE переподключения (5 секунд) */
const SSE_RECONNECT_DELAY = 5_000;

/** Максимальное количество попыток SSE переподключения */
const SSE_MAX_RECONNECT_ATTEMPTS = 10;

/** Ключ для localStorage кэша */
const CACHE_KEY = 'bus_schedule_cache';

/** Время жизни кэша (5 минут) */
const CACHE_TTL = 5 * 60 * 1000;

// ============================================
// Кэширование
// ============================================

/** Сохранить данные в localStorage */
function saveToCache(data: BusScheduleItem[]): void {
  try {
    const cache = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Игнорируем ошибки localStorage
  }
}

/** Загрузить данные из localStorage */
function loadFromCache(): BusScheduleItem[] | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;

    const cache = JSON.parse(raw) as { data: BusScheduleItem[]; timestamp: number };
    const age = Date.now() - cache.timestamp;

    // Проверяем TTL кэша
    if (age > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return cache.data;
  } catch {
    return null;
  }
}

function mapDirectionLabel(trip: ApiScheduleTrip): string {
  const direction = (trip.direction ?? '').toLowerCase();
  if (direction === 'forward') {
    return trip.routeName ?? 'Прямое направление';
  }
  if (direction === 'backward') {
    return trip.routeName ? `${trip.routeName} · обратно` : 'Обратное направление';
  }
  return trip.routeName ?? trip.direction ?? 'Маршрут';
}

function normalizeScheduleResponse(result: ApiScheduleResponse | ApiScheduleTrip[]): BusScheduleItem[] {
  const trips = Array.isArray(result) ? result : result.data?.trips ?? [];
  return trips.map((trip) => ({
    id: trip.id,
    time: trip.startTime,
    route: trip.routeNumber ?? '??',
    routeColor: trip.routeColor ?? '#999999',
    direction: mapDirectionLabel(trip),
    platform: String(trip.platform ?? '-'),
    status: trip.computedStatus ?? trip.status ?? 'scheduled',
    updatedAt: new Date().toISOString(),
  }));
}

// ============================================
// Хук
// ============================================

/**
 * Хук для загрузки и обновления расписания автобусов.
 * Поддерживает fetch-полинг, SSE real-time и оффлайн-кэш.
 */
export function useSchedule(): UseScheduleResult {
  const [data, setData] = useState<BusScheduleItem[]>(() => loadFromCache() ?? []);
  const [loading, setLoading] = useState(true);
  const [connection, setConnection] = useState<ConnectionState>('reconnecting');
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Refs для управления таймерами и SSE
  const refreshTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);

  /**
   * Загрузка данных через fetch API
   */
  const fetchData = useCallback(async (showUpdating = false) => {
    if (!isMountedRef.current) return;

    if (showUpdating) {
      setIsUpdating(true);
    }

    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`/api/schedule?date=${today}`, {
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const rawResult = (await response.json()) as ApiScheduleResponse | ApiScheduleTrip[];
      const result = normalizeScheduleResponse(rawResult);

      if (!isMountedRef.current) return;

      setData((prev) => {
        // Сравниваем с предыдущими данными, чтобы избежать лишних ререндеров
        const hasChanges =
          prev.length !== result.length ||
          JSON.stringify(prev) !== JSON.stringify(result);

        if (hasChanges) {
          saveToCache(result);
          return result;
        }
        return prev;
      });

      setConnection('online');
      setError(null);
      reconnectAttemptsRef.current = 0;
    } catch (err) {
      if (!isMountedRef.current) return;

      const message = err instanceof Error ? err.message : 'Ошибка загрузки';
      setError(message);
      setConnection('offline');

      // При ошибке сети используем кэш, если он есть
      const cached = loadFromCache();
      if (cached && cached.length > 0) {
        setData(cached);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
        setIsUpdating(false);
      }
    }
  }, []);

  /**
   * Подключение к SSE для real-time обновлений
   */
  const connectSSE = useCallback(() => {
    // Закрываем предыдущее соединение
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    if (!isMountedRef.current) return;

    setConnection((prev) => (prev === 'offline' ? 'reconnecting' : prev));

    try {
      const es = new EventSource('/api/sse/schedule-updates');
      sseRef.current = es;

      es.onopen = () => {
        if (!isMountedRef.current) return;
        setConnection('online');
        reconnectAttemptsRef.current = 0;
      };

      es.onmessage = (event) => {
        if (!isMountedRef.current) return;

        try {
          const update = JSON.parse(event.data) as
            | { type: 'full'; data: ApiScheduleResponse | ApiScheduleTrip[] }
            | { type: 'patch'; items: ApiScheduleTrip[] };

          if (update.type === 'full') {
            const normalized = normalizeScheduleResponse(update.data);
            setData(normalized);
            saveToCache(normalized);
          } else if (update.type === 'patch' && update.items) {
            // Частичное обновление — обновляем только изменённые записи
            setData((prev) => {
              const updated = [...prev];
              for (const item of normalizeScheduleResponse(update.items)) {
                const idx = updated.findIndex((r) => r.id === item.id);
                if (idx >= 0) {
                  updated[idx] = { ...updated[idx], ...item, updatedAt: new Date().toISOString() };
                } else {
                  updated.push({ ...item, updatedAt: new Date().toISOString() });
                }
              }
              // Сортируем по времени
              updated.sort((a, b) => a.time.localeCompare(b.time));
              saveToCache(updated);
              return updated;
            });
          }
        } catch {
          // Игнорируем невалидные SSE сообщения
        }
      };

      es.onerror = () => {
        if (!isMountedRef.current) return;

        es.close();
        sseRef.current = null;
        setConnection('offline');

        // Экспоненциальный backoff для переподключения
        reconnectAttemptsRef.current += 1;
        if (reconnectAttemptsRef.current <= SSE_MAX_RECONNECT_ATTEMPTS) {
          const delay = SSE_RECONNECT_DELAY * Math.min(reconnectAttemptsRef.current, 5);
          reconnectTimerRef.current = setTimeout(() => {
            if (isMountedRef.current) {
              connectSSE();
            }
          }, delay);
        }
      };
    } catch {
      // EventSource не поддерживается — используем только polling
      setConnection('offline');
    }
  }, []);

  /**
   * Ручное обновление данных
   */
  const refetch = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  // ============================================
  // Эффекты
  // ============================================

  // Начальная загрузка и SSE
  useEffect(() => {
    isMountedRef.current = true;

    // Загружаем данные
    fetchData();

    // Подключаем SSE
    connectSSE();

    // Cleanup
    return () => {
      isMountedRef.current = false;

      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (sseRef.current) {
        sseRef.current.close();
      }
    };
  }, [fetchData, connectSSE]);

  // Интервал автообновления (30 секунд)
  useEffect(() => {
    refreshTimerRef.current = setInterval(() => {
      fetchData();
    }, REFRESH_INTERVAL);

    return () => {
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
      }
    };
  }, [fetchData]);

  // Обработка online/offline событий браузера
  useEffect(() => {
    const handleOnline = () => {
      setConnection('reconnecting');
      fetchData();
      connectSSE();
    };

    const handleOffline = () => {
      setConnection('offline');
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [fetchData, connectSSE]);

  return {
    data,
    loading,
    connection,
    error,
    isUpdating,
    refetch,
  };
}
